import React, { useContext, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { FirebaseAuthContext } from "../../auth/FirebaseAuth";
import LoginDetails from "./LoginDetails/LoginDetails.component";
import OrderSummary from "./OrderSummary/OrderSummary.component";
import PaymentOptions from "./PaymentOptions/PaymentOptions.component";
import PriceDetails from "./PriceDetails/PriceDetails.component";
import './PlaceOrder.styles.scss';

const fmtDate = (d) => {
  const date = new Date(d);
  return `${String(date.getDate()).padStart(2,'0')}/${String(date.getMonth()+1).padStart(2,'0')}/${date.getFullYear()}`;
};

const getServiceFromStorage = (name) => {
  try {
    const services = JSON.parse(localStorage.getItem('cscServices') || '[]');
    return services.find((s) => s.serviceName === name) || null;
  } catch { return null; }
};

const getUploadedDocuments = (requiredDocs = []) => {
  try {
    const userDocs = JSON.parse(localStorage.getItem('userDocuments') || '{}');
    return requiredDocs
      .filter((doc) => userDocs[doc])
      .map((doc) => {
        const d = userDocs[doc];
        return d && typeof d === 'object'
          ? {
              name: doc,
              fileName: d.fileName || doc,
              fileType: d.fileType || '',
              content: d.content || '',
              uploadedAt: d.uploadedAt || '',
            }
          : { name: doc };
      });
  } catch { return []; }
};

const FEE_CATEGORIES = [
  { key: 'general', label: 'General / UR', field: 'feeGeneral' },
  { key: 'ews',     label: 'EWS',          field: 'feeEWS'     },
  { key: 'obc',     label: 'OBC',          field: 'feeOBC'     },
  { key: 'scst',    label: 'SC / ST',      field: 'feeScSt'    },
];

const getProfileCategory = () => {
  try {
    const p = JSON.parse(localStorage.getItem('userExtendedProfile') || '{}');
    const cat = p?.personal?.category;
    return FEE_CATEGORIES.some((c) => c.key === cat) ? cat : 'general';
  } catch { return 'general'; }
};

const PlaceOrder = () => {
  const { firebaseAuth } = useContext(FirebaseAuthContext);
  const user = firebaseAuth.currentUser;
  const location = useLocation();
  const { applicationName } = useParams();
  const navigate = useNavigate();
  const [paying, setPaying] = useState(false);
  const [selectedCategory] = useState(getProfileCategory);

  const { center } = location.state || {};
  const serviceName = decodeURIComponent(applicationName || '');

  const service = getServiceFromStorage(serviceName);
  const categoryEntry = FEE_CATEGORIES.find((c) => c.key === selectedCategory) || FEE_CATEGORIES[0];
  const applicationFee = Number(service?.[categoryEntry.field] ?? 0);
  const cscCharge = Number(service?.cscCharge ?? center?.charge ?? 0);
  const total = applicationFee + cscCharge;

  const handlePayNow = () => {
    setPaying(true);
    setTimeout(() => {
      const today = fmtDate(new Date());
      const finalDate = service?.endDate ? fmtDate(new Date(service.endDate + 'T00:00:00')) : '';

      const newOrder = {
        orderId: String(Date.now()),
        serviceName,
        serviceStatus: 'Applied',
        serviceLastDate: finalDate,
        amount: total,
        applicationFee,
        cscCharge,
        applicantCategory: selectedCategory,
        lastUpdated: today,
        actions: '',
        applicationSubmissionDate: today,
        applicationLastUpdatedDate: today,
        applicationFinalDate: finalDate,
        cscCenterId: center?.id ?? center?._id ?? '',
        applicant: {
          name: user?.displayName || '',
          email: user?.email || '',
          mobile: user?.phoneNumber || '',
        },
        cscCenter: {
          cscCenterId: center?.id ?? center?._id ?? '',
          cscCenterName: center?.centerName || '',
          cscCenterAddress: `${center?.address || ''}, ${center?.city || ''}, ${center?.state || ''} - ${center?.pincode || ''}`,
          cscCenterMobile: center?.mobile || '',
          cscCenterEmail: center?.email || '',
        },
        uploadedDocuments: getUploadedDocuments(service?.requiredDocuments || []),
        statusHistory: [{
          status: 'Applied',
          timestamp: new Date().toISOString(),
          updatedBy: user?.displayName || user?.email || 'Applicant',
          comment: 'Application submitted successfully.',
        }],
      };

      try {
        const existing = JSON.parse(localStorage.getItem('userApplications') || '[]');
        existing.unshift(newOrder);
        localStorage.setItem('userApplications', JSON.stringify(existing));
        navigate('/track-applications');
      } catch {
        setPaying(false);
      }
    }, 1500);
  };

  return (
    <div className="place-order-container">
      <div className="checkout-page">
        <section className="login-details-section">
          <LoginDetails user={user} />
        </section>
        <section className="order-summary-section">
          <OrderSummary center={center} serviceName={serviceName} />
        </section>
        <section className="price-details-section">
          <PriceDetails
            applicationFee={applicationFee}
            cscCharge={cscCharge}
            total={total}
            categoryLabel={categoryEntry.label}
          />
        </section>
        <section className="payment-options-section">
          <PaymentOptions />
        </section>
        <section className="pay-now-section" style={{ gridColumn: '1 / -1' }}>
          <button onClick={handlePayNow} disabled={paying} style={{ width: '100%', fontSize: '1rem', padding: '14px 0', opacity: paying ? 0.75 : 1 }}>
            {paying ? 'Processing Payment…' : `Pay Now${total > 0 ? ` — ₹${total}` : ' — Free'}`}
          </button>
        </section>
      </div>
    </div>
  );
};

export default PlaceOrder;
